import { makeStyles } from '@material-ui/styles'
import { ArrayElement } from 'core/actions/Action'
import CreateButton from 'core/components/buttons/CreateButton'
import ToggleViewSelection, { ViewMode } from 'core/components/ToggleViewSelection'
import IconButton from 'core/elements/button/IconButton'
import { BatchActionButtonProps } from 'core/elements/grid/hooks/useGridSelectableRows'
import MultiArraySearchFilter from 'core/elements/input/MultiArraySearchFilter'
import Tooltip from 'core/elements/tooltip/Tooltip'
import useToggler from 'core/hooks/useToggler'
import Theme from 'core/themes/model'
import { isReadonlyUser } from 'core/utils/helpers'
import { IDataKeys } from 'k8s/datakeys.model'
import React, { FC, PropsWithChildren, ReactNode, useMemo } from 'react'
import useReactRouter from 'use-react-router'
import { trackEvent } from 'utils/tracking'

interface AppHeaderPrimaryActionsProps<D extends keyof IDataKeys, T = ArrayElement<IDataKeys[D]>> {
  children: ReactNode
  AddButtonComponent?: FC<BatchActionButtonProps<T>>
  disableAddButton?: boolean
  disabledAddButtonInfo?: string
  addText?: string
  addUrl?: string | { (selectedItem: T, id: string): string }
  addCond?: () => boolean
  AddDialogComponent?: FC<
    PropsWithChildren<{
      onClose: () => void
    }>
  >
  isReadOnly?: boolean
  view?: ViewMode
  setView?: (view: ViewMode) => void
  HeaderActions?: ReactNode
  handleSearch?: (filteredResults: Record<string, any[]>) => void
  data?: Record<string, any[]>
  searchKeys?: string[]
  onReload?: () => void | Promise<void>
}

const useStyles = makeStyles<Theme, { isGridView: boolean }>((theme) => ({
  headerContent: {
    width: '100%',
  },
  viewModeToggleContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  label: {
    display: 'flex',
    gap: theme.spacing(1.5),
    // gap: theme.spacing(3),
    alignItems: 'center',
  },
  endLabel: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'center',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
  },
  settingsMenuButton: {
    marginLeft: 'auto',
    height: 42,
    width: 42,
  },
  refreshIcon: {
    '& i': {
      fontSize: 16,
    },
  },
}))

const AppHeaderPrimaryActions = <D extends keyof IDataKeys, T = ArrayElement<IDataKeys[D]>>({
  children,
  AddButtonComponent = CreateButton,
  disableAddButton = false,
  disabledAddButtonInfo = '',
  addText = 'Add',
  addUrl,
  AddDialogComponent,
  addCond,
  isReadOnly = isReadonlyUser(),
  view,
  setView,
  HeaderActions,
  handleSearch,
  data,
  searchKeys,
  onReload,
}: AppHeaderPrimaryActionsProps<D, T>) => {
  const isGridView = false
  const { history } = useReactRouter()
  const [showingAddDialog, toggleAddDialog] = useToggler()
  const classes = useStyles({ isGridView })

  const handleAdd = () => {
    trackEvent(`Clicked ${addText}`)
    if (addUrl) {
      history.push(addUrl)
    } else if (AddDialogComponent) {
      toggleAddDialog()
    }
  }

  const addEnabled = useMemo(() => {
    // eslint-disable-next-line no-extra-boolean-cast
    const didAddCondPass = !!addCond ? addCond() : true
    const isCustomAddButton = AddButtonComponent !== CreateButton
    return (!!AddDialogComponent || !!addUrl || isCustomAddButton) && didAddCondPass && !isReadOnly
  }, [addCond, isReadOnly, AddButtonComponent, AddDialogComponent, addUrl])

  return (
    <>
      {AddDialogComponent && showingAddDialog && (
        <AddDialogComponent onClose={toggleAddDialog}>{addText}</AddDialogComponent>
      )}
      <div className={classes.viewModeToggleContainer}>
        <div className={classes.label}>
          <ToggleViewSelection view={view} onViewChange={setView} />
          <MultiArraySearchFilter
            data={data}
            onSearch={handleSearch}
            searchKeys={searchKeys} // Can now search multiple fields
          />
          <div className={classes.refreshIcon}>
            <IconButton
              icon="refresh"
              info="Refresh"
              solid={true}
              className={classes.settingsMenuButton}
              onClick={onReload}
            />
          </div>
        </div>
        <div className={classes.endLabel}>
          {HeaderActions && <div className={classes.headerActions}>{HeaderActions}</div>}
          {addEnabled && (
            <Tooltip message={disableAddButton ? disabledAddButtonInfo : ''}>
              <AddButtonComponent disabled={disableAddButton} onClick={handleAdd}>
                {addText}
              </AddButtonComponent>
            </Tooltip>
          )}
        </div>
      </div>
      <div className={classes.headerContent}>{children}</div>
    </>
  )
}

export default AppHeaderPrimaryActions
